import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { capitalizarPalabras } from '../../../common/upperCaseWord';

/**
 * Construcción de los eventos que consume React Big Calendar.
 *
 * Funciones puras: aquí se decide qué entra al arreglo de eventos. Las tareas
 * NO se agregan cuando el usuario no puede verlas; no se ocultan con CSS.
 */

export const EVENT_KINDS = {
    CLASS: 'class',
    TASK: 'task',
};

/**
 * Tokens por color de curso (la paleta configurable del sistema: red, orange,
 * yellow, green, blue, purple). Se escriben literales para que Tailwind los
 * incluya en el build.
 */
export const COURSE_COLOR_TOKENS = {
    red: { chip: 'bg-red-50 border-red-200', accent: 'bg-red-500', text: 'text-red-700', dot: 'bg-red-500' },
    orange: { chip: 'bg-orange-50 border-orange-200', accent: 'bg-orange-500', text: 'text-orange-700', dot: 'bg-orange-500' },
    yellow: { chip: 'bg-amber-50 border-amber-200', accent: 'bg-amber-400', text: 'text-amber-700', dot: 'bg-amber-400' },
    green: { chip: 'bg-lime-50 border-lime-200', accent: 'bg-lime-500', text: 'text-lime-700', dot: 'bg-lime-500' },
    blue: { chip: 'bg-sky-50 border-sky-200', accent: 'bg-sky-500', text: 'text-sky-700', dot: 'bg-sky-500' },
    purple: { chip: 'bg-purple-50 border-purple-200', accent: 'bg-purple-500', text: 'text-purple-700', dot: 'bg-purple-500' },
    indigo: { chip: 'bg-indigo-50 border-indigo-200', accent: 'bg-indigo-500', text: 'text-indigo-700', dot: 'bg-indigo-500' },
};

const COLOR_KEYS = Object.keys(COURSE_COLOR_TOKENS);

const COLOR_NAME_ALIASES = {
    rojo: 'red',
    naranja: 'orange',
    amarillo: 'yellow',
    verde: 'green',
    azul: 'blue',
    morado: 'purple',
    purpura: 'purple',
};

const TAILWIND_TO_TOKEN = {
    red: 'red',
    orange: 'orange',
    yellow: 'yellow',
    amber: 'yellow',
    lime: 'green',
    green: 'green',
    sky: 'blue',
    blue: 'blue',
    purple: 'purple',
    indigo: 'indigo',
};

const normalizeColorName = (value) => {
    if (!value || typeof value !== 'string') return '';
    const normalized = value.trim().toLowerCase();
    return COLOR_NAME_ALIASES[normalized] || normalized;
};

const resolveColorKeyFromClase = (clase) => {
    if (!clase || typeof clase !== 'string') return null;

    const match = clase.match(/bg-(red|orange|yellow|amber|lime|green|sky|blue|purple|indigo)(?:-\d+)?/);
    if (!match) return null;

    const key = TAILWIND_TO_TOKEN[match[1]];
    return key && COURSE_COLOR_TOKENS[key] ? key : null;
};

const resolveColorKeyFromValue = (color) => {
    if (!color) return null;

    if (typeof color === 'string') {
        const fromName = normalizeColorName(color);
        if (fromName && COURSE_COLOR_TOKENS[fromName]) return fromName;
        return resolveColorKeyFromClase(color);
    }

    const fromName = normalizeColorName(color.name);
    if (fromName && COURSE_COLOR_TOKENS[fromName]) return fromName;

    return resolveColorKeyFromClase(color.clase);
};

/** Usa la etiqueta de color del curso; si falta el nombre, infiere desde la clase Tailwind. */
export const resolveCourseColorKey = (course) => {
    const fromColor = resolveColorKeyFromValue(course?.color);
    if (fromColor) return fromColor;

    const id = String(course?._id || '');
    if (!id) return 'indigo';

    const hash = [...id].reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return COLOR_KEYS[hash % COLOR_KEYS.length];
};

export const isCalendarColorKey = (key) => Boolean(key && COURSE_COLOR_TOKENS[key]);

export const courseLabel = (course) => {
    if (!course) return 'Curso';
    const parts = [course.language?.name, course.level?.name].filter(Boolean);
    if (parts.length) return parts.join(' · ');
    return course.identifier || 'Curso';
};

/** Etiqueta del curso con capitalización para UI. */
export const courseLabelDisplay = (course) => {
    if (!course) return 'Curso';
    const parts = [course.language?.name, course.level?.name]
        .filter(Boolean)
        .map((part) => capitalizarPalabras(part));
    if (parts.length) return parts.join(' · ');
    return course.identifier || 'Curso';
};

export const teacherName = (teacher) => (
    capitalizarPalabras(
        [teacher?.firstName, teacher?.lastName].filter(Boolean).join(' '),
    )
);

export const teacherFirstName = (teacher) => (
    teacher?.firstName?.trim() || teacherName(teacher)
);

const capitalizeFirst = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
};

/** Fecha de sesión en español: «Martes 1 de septiembre». */
export const formatSessionDate = (date) => capitalizeFirst(
    format(new Date(date), "EEEE d 'de' MMMM", { locale: es }),
);

/** Fecha y hora de sesión: «Martes 1 de septiembre, 08:00». */
export const formatSessionDateTime = (date) => capitalizeFirst(
    format(new Date(date), "EEEE d 'de' MMMM, HH:mm", { locale: es }),
);

/** Duración de una sesión a partir de inicio y fin (p. ej. «2 h», «1 h 30 min»). */
export const formatSessionDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return null;
    }

    const totalMinutes = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000));

    if (totalMinutes <= 0) return null;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} h`;
    return `${hours} h ${minutes} min`;
};

export const courseBannerUrl = (course) => course?.headerImage?.urlName || null;

export const COURSE_PROGRESS_DOTS = 10;

/** Porcentaje de avance del curso (0–100) según fromDate y toDate. */
export const getCourseProgressPercent = (course, now = new Date()) => {
    const from = parseCourseDate(course?.fromDate);
    const to = parseCourseDate(course?.toDate);

    if (!from || !to || to <= from) return 0;

    const ratio = (now.getTime() - from.getTime()) / (to.getTime() - from.getTime());
    const clamped = Math.max(0, Math.min(1, ratio));

    return Math.round(clamped * 100);
};

/** Puntos llenos (0–total) para la barra de progreso del curso. */
export const getCourseProgressDots = (
    course,
    total = COURSE_PROGRESS_DOTS,
    now = new Date(),
) => {
    const percent = getCourseProgressPercent(course, now);
    if (percent <= 0) return 0;
    return Math.min(total, Math.max(1, Math.ceil((percent / 100) * total)));
};

/** Segmentos llenos (0–4) según avance del curso entre fromDate y toDate. */
export const getCourseProgressSegments = (course, now = new Date()) => (
    getCourseProgressDots(course, 4, now)
);

const parseCourseDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Rango legible para fichas y popover: «31 Ago – 26 Sep 2026». */
export const formatCourseDateRange = (course) => {
    const from = parseCourseDate(course?.fromDate);
    const to = parseCourseDate(course?.toDate);

    const formatLabel = (value) => capitalizarPalabras(
        format(value, 'd MMM yyyy', { locale: es }).replace(/\./g, ''),
    );

    if (from && to) {
        const sameYear = from.getFullYear() === to.getFullYear();
        const fromLabel = capitalizarPalabras(
            format(from, sameYear ? 'd MMM' : 'd MMM yyyy', { locale: es }).replace(/\./g, ''),
        );
        const toLabel = formatLabel(to);
        return `${fromLabel} – ${toLabel}`;
    }

    if (from) return `Desde ${formatLabel(from)}`;
    if (to) return `Hasta ${formatLabel(to)}`;
    return null;
};

/** Días con eventos para el mini calendario (`yyyy-MM-dd`). */
export const collectMarkedDayKeys = (events = []) => {
    const keys = new Set();
    events.forEach((event) => {
        if (!event?.start) return;
        const date = event.start instanceof Date ? event.start : new Date(event.start);
        if (Number.isNaN(date.getTime())) return;
        keys.add(format(date, 'yyyy-MM-dd'));
    });
    return keys;
};

/** Versión compacta para espacios reducidos. */
export const formatCourseDateRangeShort = (course) => {
    const from = parseCourseDate(course?.fromDate);
    const to = parseCourseDate(course?.toDate);

    if (from && to) {
        const sameYear = from.getFullYear() === to.getFullYear();
        const fromLabel = format(from, 'd/M', { locale: es });
        const toLabel = format(to, sameYear ? 'd/M' : 'd/M/yy', { locale: es });
        return `${fromLabel}–${toLabel}`;
    }

    if (from) return format(from, 'd/M/yy', { locale: es });
    if (to) return format(to, 'd/M/yy', { locale: es });
    return null;
};

const isPastClassEvent = (event) => {
    const status = event.resource?.classSession?.temporalStatus;
    if (status === 'past') return true;
    if (status === 'live' || status === 'upcoming') return false;
    return event.end < new Date();
};

export const ACTIVITY_PANEL_TITLES = {
    day: 'Actividades del día',
    week: 'Actividades de la semana',
    month: 'Actividades del mes',
};

export const getActivityPanelTitle = (view) => (
    ACTIVITY_PANEL_TITLES[view] || 'Actividades'
);

/**
 * Idiomas disponibles, derivados de los cursos que el usuario puede ver.
 * @returns {{id: string, name: string, path: string|null}[]}
 */
export const deriveLanguageOptions = (courses = []) => {
    const byId = new Map();

    courses.forEach((course) => {
        const language = course?.language;
        if (!language?._id) return;

        const id = String(language._id);
        if (byId.has(id)) return;

        byId.set(id, {
            id,
            name: language.name || 'Sin idioma',
            path: language.path || null,
        });
    });

    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
};

/**
 * Maestros con curso visible en el calendario.
 * @returns {{id: string, name: string, firstName: string, lastName: string}[]}
 */
export const deriveTeacherOptions = (courses = []) => {
    const byId = new Map();

    courses.forEach((course) => {
        const teacher = course?.teacher;
        if (!teacher?._id) return;

        const id = String(teacher._id);
        if (byId.has(id)) return;

        byId.set(id, {
            id,
            firstName: teacher.firstName || '',
            lastName: teacher.lastName || '',
            name: teacherName(teacher),
        });
    });

    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
};

const toStringSet = (values) => new Set((values || []).map(String));

const intersectCourseIdSets = (left, right) => (
    new Set([...left].filter((id) => right.has(id)))
);

/** Cursos disponibles para poblar opciones del sidebar (filtros cruzados). */
export const filterCoursesForSidebarOptions = (
    courses = [],
    { selectedTeacherIds = [], selectedLanguageIds = [], exclude = null } = {},
) => courses.filter((course) => {
    if (exclude !== 'teacher' && selectedTeacherIds.length) {
        const selected = toStringSet(selectedTeacherIds);
        if (!course?.teacher?._id || !selected.has(String(course.teacher._id))) return false;
    }
    if (exclude !== 'language' && selectedLanguageIds.length) {
        const selected = toStringSet(selectedLanguageIds);
        if (!course?.language?._id || !selected.has(String(course.language._id))) return false;
    }
    return true;
});

/**
 * Aplica los filtros compartidos de /cursos (el drawer de filtros) sobre los
 * cursos que el servidor ya autorizó. Nunca amplía la visibilidad: solo reduce.
 *
 * @param {object} filters identifier, teacher, language[], level[], status[], days[]
 */
export const applyCourseFilters = (courses = [], filters = {}) => {
    const {
        identifier = '',
        teacher = '',
        language = [],
        level = [],
        status = [],
        days = [],
    } = filters;

    const languageIds = toStringSet(language);
    const levelIds = toStringSet(level);
    const statuses = toStringSet(status);
    const dayNames = toStringSet(days);

    return courses.filter((course) => {
        if (identifier && course.identifier !== identifier) return false;
        if (teacher && String(course.teacher?._id) !== String(teacher)) return false;
        if (languageIds.size && !languageIds.has(String(course.language?._id))) return false;
        if (levelIds.size && !levelIds.has(String(course.level?._id))) return false;
        if (statuses.size && !statuses.has(course.status)) return false;

        if (dayNames.size) {
            const matchesDay = (course.days || []).some((day) => dayNames.has(day.day));
            if (!matchesDay) return false;
        }

        return true;
    });
};

/** Ids de cursos que pasan el filtro de maestros (vacío = todos). */
export const filterCourseIdsByTeacher = (courses = [], selectedTeacherIds = []) => {
    if (!selectedTeacherIds.length) {
        return new Set(courses.map((course) => String(course._id)));
    }

    const selected = toStringSet(selectedTeacherIds);

    return new Set(
        courses
            .filter((course) => course?.teacher?._id && selected.has(String(course.teacher._id)))
            .map((course) => String(course._id)),
    );
};

/** Ids de cursos que pasan el filtro de idiomas (vacío = todos). */
export const filterCourseIdsByLanguage = (courses = [], selectedLanguageIds = []) => {
    if (!selectedLanguageIds.length) {
        return new Set(courses.map((course) => String(course._id)));
    }

    const selected = new Set(selectedLanguageIds.map(String));

    return new Set(
        courses
            .filter((course) => course?.language?._id && selected.has(String(course.language._id)))
            .map((course) => String(course._id)),
    );
};

const TASK_BLOCK_MS = 45 * 60 * 1000;

/**
 * Día que ocupa la tarea en el calendario:
 * aún no activa → fecha de activación; ya activa → fecha límite.
 */
export const taskCalendarDate = (task) => {
    if (task?.windowState === 'upcoming' && task.activationDate) {
        return new Date(task.activationDate);
    }
    return new Date(task.dueDate);
};

/** Bloque de 45 min que cabe en el mismo día, pegado al final si la hora es 23:59. */
export const taskEventRange = (task) => {
    const when = taskCalendarDate(task);
    const endOfDay = new Date(when);
    endOfDay.setHours(23, 59, 0, 0);

    let start = new Date(when);
    let end = new Date(when.getTime() + TASK_BLOCK_MS);

    if (end > endOfDay) {
        end = endOfDay;
        start = new Date(end.getTime() - TASK_BLOCK_MS);
        if (start.getDate() !== end.getDate() || start > end) {
            start = new Date(end);
            start.setHours(23, 14, 0, 0);
        }
    }

    return { start, end };
};

/**
 * @param {object} params
 * @param {boolean} params.canViewTasks Cuando es false, ninguna tarea entra al arreglo.
 * @param {object} params.filters Filtros compartidos de /cursos.
 * @param {string[]} params.selectedLanguageIds Filtro de idioma propio del calendario.
 * @param {string[]} params.selectedTeacherIds Filtro de maestro (director / desarrollador).
 * @returns {Array} eventos para React Big Calendar
 */
export const buildCalendarEvents = ({
    courses = [],
    classes = [],
    tasks = [],
    canViewTasks = false,
    filters = {},
    selectedLanguageIds = [],
    selectedTeacherIds = [],
} = {}) => {
    const courseById = new Map(courses.map((course) => [String(course._id), course]));
    const filteredCourses = applyCourseFilters(courses, filters);
    const languageIds = filterCourseIdsByLanguage(filteredCourses, selectedLanguageIds);
    const teacherIds = filterCourseIdsByTeacher(filteredCourses, selectedTeacherIds);
    const allowedCourseIds = intersectCourseIdSets(languageIds, teacherIds);

    const classEvents = classes
        .filter((item) => allowedCourseIds.has(String(item.courseId)))
        .map((item) => {
            const course = courseById.get(String(item.courseId));
            return {
                id: `class-${item.id}`,
                kind: EVENT_KINDS.CLASS,
                title: item.topic || courseLabel(course),
                start: new Date(item.scheduledStart),
                end: new Date(item.scheduledEnd),
                resource: {
                    kind: EVENT_KINDS.CLASS,
                    course,
                    colorKey: resolveCourseColorKey(course),
                    classSession: item,
                },
            };
        });

    if (!canViewTasks) {
        return classEvents;
    }

    const taskEvents = tasks
        .filter((task) => allowedCourseIds.has(String(task.courseId)))
        .map((task) => {
            const course = courseById.get(String(task.courseId));
            const { start, end } = taskEventRange(task);
            return {
                id: `task-${task._id}`,
                kind: EVENT_KINDS.TASK,
                title: task.title,
                allDay: false,
                start,
                end,
                resource: {
                    kind: EVENT_KINDS.TASK,
                    course,
                    colorKey: resolveCourseColorKey(course),
                    task,
                },
            };
        });

    return [...classEvents, ...taskEvents];
};

/** Eventos cuyo inicio cae dentro del rango visible del calendario (día, semana o mes). */
export const filterEventsByRange = (events = [], range) => {
    if (!range?.from || !range?.to) return events;

    const fromTime = range.from.getTime();
    const toTime = range.to.getTime();

    return events.filter((event) => {
        const start = event.start instanceof Date ? event.start : new Date(event.start);
        if (Number.isNaN(start.getTime())) return false;
        const time = start.getTime();
        return time >= fromTime && time <= toTime;
    });
};

/**
 * Grupos del acordeón de Actividades para el rango visible.
 * - pending: activas, aún por entregar
 * - upcoming: aún no se activan (solo el profesor las ve en el listado)
 */
export const groupSidebarTasks = (events = [], { showUpcoming = false } = {}) => {
    const pending = [];
    const upcoming = [];

    events.forEach((event) => {
        if (event.resource?.kind !== EVENT_KINDS.TASK) return;
        const task = event.resource.task;
        if (task?.submitted) return;

        if (task?.windowState === 'upcoming') {
            if (showUpcoming) upcoming.push(event);
            return;
        }

        if (task?.windowState === 'active') pending.push(event);
    });

    const byDate = (a, b) => a.start - b.start;
    pending.sort(byDate);
    upcoming.sort((a, b) => {
        const aAt = new Date(a.resource?.task?.activationDate || a.start);
        const bAt = new Date(b.resource?.task?.activationDate || b.start);
        return aAt - bAt;
    });

    return { pending, upcoming };
};

/** Alias conservado para pruebas anteriores. */
export const groupTaskEvents = (events, options) => groupSidebarTasks(events, options);

/** Conteos para Actividades: solo sesiones y tareas que aún no ocurrieron. */
export const summarizeEvents = (events = []) => {
    const summary = {
        total: events.length,
        classes: 0,
        classesLive: 0,
        classesPast: 0,
        classesUpcoming: 0,
        tasks: 0,
        tasksPending: 0,
        tasksSubmitted: 0,
        tasksOverdue: 0,
    };

    events.forEach((event) => {
        const { kind, classSession } = event.resource || {};
        if (kind !== EVENT_KINDS.CLASS) return;

        if (isPastClassEvent(event)) {
            summary.classesPast += 1;
            return;
        }

        summary.classes += 1;
        if (classSession?.temporalStatus === 'live') summary.classesLive += 1;
        else summary.classesUpcoming += 1;
    });

    return summary;
};
